import { useState } from "react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { normalizeSymbol } from "../../../shared/utils";
import { pnlColor } from "../chart/chartColors";

type SortKey = "totalPnl" | "trades" | "winRate";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function SymbolPerformanceWidget() {
  const { analyticsData, isDark, t } = useDashboardData();
  const [sortKey, setSortKey] = useState<SortKey>("totalPnl");

  if (!analyticsData || analyticsData.symbolStats.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const sorted = [...analyticsData.symbolStats].sort((a, b) => {
    if (sortKey === "totalPnl") return b.totalPnl - a.totalPnl;
    if (sortKey === "trades") return b.trades - a.trades;
    return b.winRate - a.winRate;
  });

  const SORT_OPTIONS: { value: SortKey; label: string }[] = [
    { value: "totalPnl", label: t.analytics.totalPnl },
    { value: "trades", label: t.journal.trades },
    { value: "winRate", label: t.analytics.winRate },
  ];

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.analytics.symbolsLabel}
        title={t.analytics.topSymbols}
        isDark={isDark}
        inline
        rightContent={
          <div
            className={`flex rounded border overflow-hidden text-xs ${
              isDark ? "border-border" : "border-gray-200"
            }`}
          >
            {SORT_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSortKey(value)}
                className={`px-2 py-1 transition-colors font-medium ${
                  sortKey === value
                    ? "bg-primary text-primary-foreground"
                    : isDark
                    ? "widget-label hover:text-foreground"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />
      <CardContent className="px-5 pb-5 pt-1">
        <div className="overflow-auto max-h-64">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b">
                <th className="text-left p-2 text-xs font-medium text-muted-foreground">
                  {t.analytics.symbol}
                </th>
                <th className="text-right p-2 text-xs font-medium text-muted-foreground">
                  {t.journal.trades}
                </th>
                <th className="text-right p-2 text-xs font-medium text-muted-foreground">
                  {t.analytics.winRate}
                </th>
                <th className="text-right p-2 text-xs font-medium text-muted-foreground">
                  {t.analytics.totalPnl}
                </th>
                <th className="text-right p-2 text-xs font-medium text-muted-foreground">
                  {t.analytics.averagePnl}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 15).map((stat) => (
                <tr
                  key={stat.symbol}
                  className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="p-2 font-medium font-mono">
                    {normalizeSymbol(stat.symbol)}
                  </td>
                  <td className="p-2 text-right tabular-nums">{stat.trades}</td>
                  <td className="p-2 text-right tabular-nums">
                    <span
                      className={
                        stat.winRate >= 50 ? "text-primary" : "text-destructive"
                      }
                    >
                      {stat.winRate.toFixed(1)}%
                    </span>
                  </td>
                  <td
                    className="p-2 text-right font-medium tabular-nums"
                    style={{
                      color: pnlColor(stat.totalPnl),
                    }}
                  >
                    {stat.totalPnl >= 0 ? "+" : ""}
                    {stat.totalPnl.toFixed(2)} $
                  </td>
                  <td
                    className="p-2 text-right tabular-nums"
                    style={{
                      color: pnlColor(stat.averagePnl),
                    }}
                  >
                    {stat.averagePnl >= 0 ? "+" : ""}
                    {stat.averagePnl.toFixed(2)} $
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
